import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Avatar, Box, ButtonBase, Divider, IconButton, InputBase, Popover, Tooltip, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { getSVG } from '../utils/getSvg'

/**
 * The header's one context control: what the page shows (restaurant, branch, and any extra scope
 * like a currency) on a single button, the choices in a panel below it.
 *
 * Purely presentational — the caller (RestaurantBranchSelector) decides which sections exist and
 * what selecting does, so every page keeps its own rules (no restaurant picker, no branch, …).
 *
 * `sections`: `[{ key, multiple?, chips?, items: [{ key, label, caption?, disabled?, selected,
 * image?, initials?, icon?: 'pin' | 'all', onSelect }] }]` — a list section closes the panel on a
 * pick unless it is `multiple`; a `chips` section is a row of pills (a currency).
 *
 * `search` (`{ placeholder, empty }`): a search field over the restaurant / branch lists, matching
 * names whatever their case or accents. `compact` (phones) keeps the names on the button, smaller.
 * `bar` (phones, see the headers' ContextBar): the control is a full-width row of its own under the
 * header — no border, the names on one line, the arrow at the far end.
 */

/** Text as it is compared by the panel's search: no case, no accents. */
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g')
const fold = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()

/** Focus the search only where typing is the way in — on a touch screen it would pop the keyboard over the list. */
const focusSearchOnOpen = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(hover: hover)').matches)
const initialsOf = (label) => {
  const words = String(label || '').trim().split(/\s+/).filter(Boolean)
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '?').slice(0, 2)).toUpperCase()
}

const ContextSelect = ({ title, subtitle, avatar, sections, compact, bar, disabled, surface, search }) => {
  const theme = useTheme()
  const isRTL = theme.direction === 'rtl'
  const triggerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const close = () => {
    setOpen(false)
    setQuery('')
  }
  const wanted = fold(query).trim()
  const matches = (item) => !wanted || fold(`${item.label} ${item.caption || ''}`).includes(wanted)
  const shown = sections.filter((section) => section.items.length > 0)
  const interactive = !disabled && shown.some((section) => section.multiple || section.items.length > 1)
  const side = isRTL ? 'right' : 'left'

  const avatarBox = (size, { image, initials, icon } = {}, tinted = false) => {
    if (image) return <Avatar src={image} variant="rounded" sx={{ width: size, height: size, borderRadius: size > 28 ? '8px' : '7px', flexShrink: 0 }} />
    if (icon) {
      return (
        <Box sx={{ width: size, height: size, borderRadius: size > 28 ? '8px' : '7px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(theme.palette.primary.main, 0.12) }}>
          {getSVG(icon === 'all' ? 'lucide-network' : 'lucide-map-pin', theme.palette.primary.main, { width: size * 0.55, height: size * 0.55 }, undefined, undefined, true)}
        </Box>
      )
    }
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: size > 28 ? '8px' : '7px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size > 28 ? 12 : 11,
          fontWeight: 700,
          ...(tinted
            ? { backgroundColor: alpha(theme.palette.primary.main, 0.14), color: theme.palette.primary.main }
            : { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }),
        }}
      >
        {initials}
      </Box>
    )
  }

  const check = (color) => getSVG('lucide-check', color, { width: 16, height: 16 }, undefined, undefined, true)
  const listSections = shown.filter((section) => !section.chips)
  const chipSections = shown.filter((section) => section.chips)

  const renderItem = (section) => (item) => (
    <ButtonBase
      key={item.key}
      role={section.multiple ? 'menuitemcheckbox' : 'menuitemradio'}
      aria-checked={item.selected}
      disabled={item.disabled}
      onClick={() => {
        item.onSelect()
        if (!section.multiple) close()
      }}
      sx={{
        width: '100%',
        flexShrink: 0,
        justifyContent: 'flex-start',
        gap: 1.25,
        px: 1,
        py: 0.75,
        minHeight: 40,
        borderRadius: '10px',
        textAlign: 'start',
        color: theme.palette.text.primary,
        backgroundColor: item.selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
        '&:hover': { backgroundColor: item.selected ? alpha(theme.palette.primary.main, 0.14) : alpha(theme.palette.text.primary, 0.05) },
        '&.Mui-disabled': { opacity: 0.55 },
      }}
    >
      {avatarBox(26, { image: item.image, initials: item.initials || initialsOf(item.label), icon: item.icon }, true)}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: item.selected ? 600 : 400 }}>
          {item.label}
        </Typography>
        {item.caption && (
          <Typography noWrap sx={{ fontSize: 11.5, fontStyle: 'italic', color: theme.palette.text.secondary }}>
            {item.caption}
          </Typography>
        )}
      </Box>
      {item.selected && <Box sx={{ display: 'flex', flexShrink: 0 }}>{check(theme.palette.primary.main)}</Box>}
    </ButtonBase>
  )

  const trigger = (
    <ButtonBase
      ref={triggerRef}
      disabled={!interactive}
      onClick={() => setOpen(true)}
      aria-haspopup={interactive ? 'menu' : undefined}
      aria-expanded={interactive ? open : undefined}
      aria-label={compact ? [title, subtitle].filter(Boolean).join(', ') : undefined}
      sx={bar ? {
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        minWidth: 0,
        height: 36,
        pl: '4px',
        pr: 1,
        borderRadius: '10px',
        backgroundColor: open ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
        color: theme.palette.text.primary,
        textAlign: 'start',
        transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.shorter }),
        '&:hover': interactive ? { backgroundColor: alpha(theme.palette.text.primary, 0.05) } : undefined,
        '&.Mui-disabled': { color: theme.palette.text.primary },
        ...(disabled && { opacity: 0.6 }),
      } : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 1 : 1.25,
        flexShrink: 1,
        minWidth: 0,
        maxWidth: compact ? '100%' : 320,
        height: compact ? 40 : 42,
        pl: '5px',
        pr: compact ? '6px' : '10px',
        borderRadius: '12px',
        border: 1,
        borderColor: open ? theme.palette.primary.main : theme.palette.divider,
        boxShadow: open ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
        backgroundColor: surface === 'paper' ? theme.palette.background.paper : theme.palette.background.default,
        color: theme.palette.text.primary,
        textAlign: 'start',
        transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: theme.transitions.duration.shorter }),
        '&:hover': interactive ? { borderColor: alpha(theme.palette.primary.main, 0.5) } : undefined,
        '&.Mui-disabled': { color: theme.palette.text.primary },
        ...(disabled && { opacity: 0.6 }),
      }}
    >
      {avatarBox(compact || bar ? 28 : 30, avatar)}
      {bar ? (
        // One line, the restaurant first: whatever is cut, it is the end of the branch.
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ flex: '0 1 auto', minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography noWrap sx={{ flex: '0 1 auto', minWidth: '30%', fontSize: 12.5, color: theme.palette.text.secondary }}>
              {`· ${subtitle}`}
            </Typography>
          )}
        </Box>
      ) : (
        // The names stay on a phone too (smaller, cut to the room there is): which restaurant and
        // branch is open. No cap of their own there — every pixel the header can spare goes to them.
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2 }}>
          <Typography noWrap sx={{ fontSize: compact ? 12.5 : 14, fontWeight: 600, lineHeight: 1.25 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography noWrap sx={{ fontSize: compact ? 11 : 12, color: theme.palette.text.secondary, lineHeight: 1.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {interactive && (
        <Box sx={{ display: 'flex', flexShrink: 0, ...(bar && { ml: 'auto' }), transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}>
          {getSVG('lucide-chevron-down', theme.palette.text.secondary, { width: 16, height: 16 }, undefined, undefined, true)}
        </Box>
      )}
    </ButtonBase>
  )

  return (
    <>
      {compact ? <Tooltip title={[title, subtitle].filter(Boolean).join(' · ')}>{trigger}</Tooltip> : trigger}
      {interactive && (
        <Popover
          open={open}
          anchorEl={triggerRef.current}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: side }}
          transformOrigin={{ vertical: 'top', horizontal: side }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 0,
              width: listSections.length > 1 ? 'min(560px, calc(100vw - 32px))' : undefined,
              // As wide as the control, but never wider than the screen (the phone bar spans it).
              minWidth: `min(${Math.max(triggerRef.current?.offsetWidth || 0, 280)}px, calc(100vw - 32px))`,
              maxWidth: listSections.length > 1 ? 640 : 360,
              maxHeight: '80vh',
              borderRadius: '14px',
              border: 1,
              borderColor: 'divider',
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <Box role="menu">
            {search && listSections.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                {getSVG('lucide-search', theme.palette.text.secondary, { width: 16, height: 16 }, undefined, undefined, true)}
                <InputBase
                  autoFocus={focusSearchOnOpen()}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={search.placeholder}
                  inputProps={{ 'aria-label': search.placeholder }}
                  sx={{ flex: 1, minWidth: 0, fontSize: 13.5, height: 36 }}
                />
                {query && (
                  <IconButton size="small" aria-label={search.clear} onClick={() => setQuery('')} sx={{ p: 0.5 }}>
                    {getSVG('lucide-x', theme.palette.text.secondary, { width: 14, height: 14 }, undefined, undefined, true)}
                  </IconButton>
                )}
              </Box>
            )}
            {/* Restaurants and branches side by side, each scrolling on its own; stacked on a phone. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: listSections.length > 1 ? { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } : 'minmax(0, 1fr)',
              }}
            >
              {listSections.map((section, index) => (
                <Box
                  key={section.key}
                  role="group"
                  sx={{
                    minWidth: 0,
                    maxHeight: 'min(420px, 60vh)',
                    overflowY: 'auto',
                    p: 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.15), borderRadius: 3 },
                    ...(index > 0 && {
                      borderTop: { xs: 1, sm: 0 },
                      borderLeft: { xs: 0, sm: 1 },
                      borderColor: 'divider',
                    }),
                  }}
                >
                  {section.items.filter(matches).map(renderItem(section))}
                  {wanted && !section.items.some(matches) && (
                    <Typography sx={{ px: 1, py: 1.25, fontSize: 12.5, color: theme.palette.text.secondary }}>{search?.empty}</Typography>
                  )}
                </Box>
              ))}
            </Box>
            {chipSections.map((section) => (
              <React.Fragment key={section.key}>
                {listSections.length > 0 && <Divider />}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, p: 1.25 }}>
                  {section.items.map((item) => (
                    <ButtonBase
                      key={item.key}
                      role="menuitemradio"
                      aria-checked={item.selected}
                      disabled={item.disabled}
                      onClick={() => {
                        item.onSelect()
                        close()
                      }}
                      sx={{
                        height: 32,
                        px: 1.5,
                        borderRadius: '999px',
                        fontSize: 13,
                        fontWeight: 600,
                        border: 1,
                        borderColor: item.selected ? theme.palette.primary.main : theme.palette.divider,
                        backgroundColor: item.selected ? theme.palette.primary.main : 'transparent',
                        color: item.selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                        '&:hover': { borderColor: theme.palette.primary.main },
                      }}
                    >
                      {item.label}
                    </ButtonBase>
                  ))}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Popover>
      )}
    </>
  )
}

ContextSelect.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  avatar: PropTypes.shape({ image: PropTypes.string, initials: PropTypes.string, icon: PropTypes.oneOf(['pin', 'all']) }),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      multiple: PropTypes.bool,
      chips: PropTypes.bool,
      items: PropTypes.arrayOf(PropTypes.object).isRequired,
    })
  ).isRequired,
  compact: PropTypes.bool,
  bar: PropTypes.bool,
  disabled: PropTypes.bool,
  surface: PropTypes.oneOf(['default', 'paper']),
  search: PropTypes.shape({ placeholder: PropTypes.string, empty: PropTypes.string, clear: PropTypes.string }),
}

ContextSelect.defaultProps = {
  subtitle: '',
  avatar: {},
  compact: false,
  bar: false,
  disabled: false,
  surface: 'default',
  search: null,
}

export { initialsOf }
export default ContextSelect
